import type { Element, SpellId } from '@archon3d/core';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';

/** Table scale: a 9×9 board is 0.9 m across, like a chess board on a table. */
export const TILE_SIZE = 0.1;
/** Top surface of the table. */
export const TABLE_HEIGHT = 0.86;
export const TABLE_TOP_THICKNESS = 0.05;
/** Circumradius of the six-sided table top; its inradius (0.87 × this) must exceed the board's half-diagonal. */
export const TABLE_RADIUS = 0.82;
/**
 * Yaw of the six-sided table top. Babylon's prism puts its first corner on +X (measured with a
 * null engine: corners at 0°, ±60°, ±120°, 180° from +X), so at 0 a flat side faces the player
 * along +Z and the corners point left and right. A hex board's frame is the same prism and turns
 * with this, so the two stay parallel however the table is turned.
 */
export const TABLE_YAW = 0;
/** Boards whose half-diagonal exceeds this are shrunk to fit inside the table top. */
export const BOARD_FIT_RADIUS = TABLE_RADIUS * 0.866 - 0.02;
/** In front of where a VR player stands when the session starts (they face −Z). */
export const BOARD_CENTER = new Vector3(0, TABLE_HEIGHT, -0.7);
/** Shared far-side landmark for the turn sign and the adjacent immersive controls. */
export const FAR_TABLET_LAYOUT = {
  width: 0.5,
  height: 0.327,
  y: TABLE_HEIGHT + 0.34,
  z: BOARD_CENTER.z - 0.86,
  tilt: (70 * Math.PI) / 180,
} as const;
export const FRAME_THICKNESS = 0.02;
export const TILE_THICKNESS = 0.014;
export const BOARD_TOP_Y = TABLE_HEIGHT + FRAME_THICKNESS + TILE_THICKNESS;
/** Walls stand this high above the tiles on either grid: waist height to the pieces. */
export const WALL_HEIGHT = 0.035;
export const WALL_THICKNESS = 0.012;
/**
 * How far off the middle of a contested square each of its two pieces stands while their fight is
 * pending, in tiles. They end up this much either side of the centre — far enough apart to read as
 * two pieces making room for each other, near enough that the square is plainly one square.
 */
export const CHALLENGE_OFFSET = 0.3;

export const COLORS = {
  light: '#ecd57f',
  dark: '#2c46b8',
  cycling: '#7d5fb3',
  powerPoint: '#ff5a36',
  frame: '#2a2530',
  wall: '#4a4152',
  floor: '#1a1c26',
  highlightMove: '#5ef2c9',
  highlightAttack: '#ff5a5a',
  highlightSpell: '#c48bff',
  /** The squares the last move went from and to, faintly lit (x-aaa-gap#18). */
  highlightLastMove: '#c9e265',
  selection: '#ffffff',
  /** The ring under the cell the cursor rests on: the mouse, or a gamepad's steps. */
  hover: '#f6efd9',
  /** The dark band under the selection and hover rings, which outlines them on a light square. */
  ringBand: '#141620',
  /** Warning rings under pieces an enemy could profitably capture. */
  threat: '#ffb340',
} as const;

/** Colours of the cycling squares for luminosity phases 0 (darkest) … 5 (brightest). */
export const LUMINOSITY_SHADES = [
  '#152366',
  '#2c46b8',
  '#7d5fb3',
  '#b58fbf',
  '#dcc3a0',
  '#ecd57f',
] as const;

/** The four elements, for the elementals' placeholders and the summon burst. */
export const ELEMENT_COLORS: Record<Element, string> = {
  earth: '#b08a3e',
  water: '#3d8bff',
  air: '#dff5ff',
  fire: '#ff7a2a',
};

/** What each spell's effect on the board glows in; a summon takes its element's colour. */
export const SPELL_COLORS: Record<Exclude<SpellId, 'summon-elemental'>, string> = {
  teleport: '#c48bff',
  heal: '#62e86a',
  'shift-time': '#ffe9a6',
  // Sickly green for a curse, pale blue for a freeze, clean gold for the spell that undoes them.
  curse: '#8fbf5a',
  freeze: '#9fdcff',
  release: '#ffd98a',
  exchange: '#e0c4ff',
  revive: '#fff2c0',
  imprison: '#8a4cff',
};
